// @ts-check

/**
 * Hero marquee (infinite horizontal card scroll).
 *
 * The markup ships one "set" of cards plus a couple of clones, and the track
 * is animated with `translateX(0 → -100%/copies)`. A fixed 2-copy / -50% loop
 * is only seamless when ONE set is at least as wide as the viewport — on any
 * wider screen the track runs out of content and a growing blank gap appears
 * on the right (the "next card never arrives" symptom).
 *
 * This component measures one set and clones it enough times that the loop
 * can never expose a gap, then drives the animation distance via the
 * `--marquee-shift` / `--marquee-duration` custom properties so speed stays
 * constant regardless of how many copies end up in the track.
 */
export class HeroMarquee {
  /** @param {HTMLElement} container — the `.hero-marquee` element */
  constructor(container) {
    this.container = container;
    const track = container.querySelector('.hero-marquee-track');
    /** @type {HTMLElement|null} */
    this.track = track instanceof HTMLElement ? track : null;
    if (!this.track) return;

    // The authored cards are the source set; everything with aria-hidden is a
    // generated clone and gets rebuilt on every layout pass.
    const authored = /** @type {HTMLElement[]} */ (
      Array.from(this.track.querySelectorAll('.hero-card')).filter(
        (el) => el.getAttribute('aria-hidden') !== 'true'
      )
    );
    this.base = authored.length
      ? authored
      : /** @type {HTMLElement[]} */ (
          Array.from(this.track.querySelectorAll('.hero-card'))
        );
    if (this.base.length === 0) return;

    this._resizeRaf = 0;
    /** @param {Event} e */
    this._handleResize = () => {
      if (this._resizeRaf) return;
      this._resizeRaf = window.requestAnimationFrame(() => {
        this._resizeRaf = 0;
        this.layout();
      });
    };

    this.layout();
    window.addEventListener('resize', this._handleResize, { passive: true });
  }

  /** Rebuild clones and animation metrics from the current viewport. */
  layout() {
    const track = this.track;
    if (!track) return;

    // Drop previously generated clones.
    track
      .querySelectorAll('.hero-card[aria-hidden="true"]')
      .forEach((el) => el.remove());

    // Width of ONE set (sum of authored cards). offsetWidth ignores transforms.
    const setWidth = this.base.reduce((sum, card) => sum + card.offsetWidth, 0);
    if (setWidth <= 0) return;

    const viewport = window.innerWidth;
    // Total width must exceed (viewport + one set) so that, at the end of the
    // animation (shifted by exactly one set), the track still covers the
    // whole viewport.
    const copies = Math.max(2, Math.ceil((viewport + setWidth) / setWidth));

    for (let i = 1; i < copies; i += 1) {
      this.base.forEach((card) => {
        const clone = /** @type {HTMLElement} */ (card.cloneNode(true));
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        track.appendChild(clone);
      });
    }

    // Animate by exactly one set width; keep the scroll speed constant.
    track.style.setProperty('--marquee-shift', `-${setWidth}px`);
    const duration = setWidth / 36; // ≈ 28s for the original 1020px set
    track.style.setProperty('--marquee-duration', `${duration.toFixed(2)}s`);
  }

  destroy() {
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
    window.removeEventListener('resize', this._handleResize);
  }
}
